/**
 * Çok dilli rota tanımları (N-locale genel mimari).
 *
 * TR (varsayılan, prefiks yok): /, /oyna, /gunluk, /cozucu, /egitim, /egitim/:id, /nasil-oynanir, /siralama
 * EN: /en, /en/play, /en/daily, /en/solver, /en/education, /en/education/:id, /en/tutorial, /en/leaderboard
 * NL/FR/DE/IT/PL/RU/ES: aynı desen, kendi slug'larıyla — ROUTE_SLUGS'a bakın.
 *
 * Eski EN-stil TR url'leri (/play, /daily, /education, /education/:id, /solver, /tutorial, /leaderboard)
 * 301 redirect ile TR slug'larına yönlendirilir (sadece TR için, legacy).
 *
 * Noindex sayfalar (auth/lobby/challenge/profile/friends/premium) tek URL'le
 * her dilde aynı çalışır — locale'e göre path değişmez.
 *
 * NOT: ROUTE_SLUGS burada TÜM dilleri (ALL_LOCALES) içerir — bu sadece mimari
 * desteği, gerçek çeviri olmadan bu route'lar canlıya/sitemap'e çıkmaz (bkz.
 * locales.ts'teki LIVE_LOCALES). AppRouter.tsx önizleme amacıyla tüm diller
 * için route oluşturur; LangToggle/sitemap/prerender ise sadece LIVE_LOCALES'i
 * kullanır.
 */

import type { Locale } from './locales';
import { ALL_LOCALES } from './locales';

export type RouteKey =
  | 'download'
  | 'home'
  | 'play'
  | 'daily'
  | 'solver'
  | 'education'
  | 'tutorial'
  | 'leaderboard'
  | 'auth'
  | 'lobby'
  | 'challenge'
  | 'profile'
  | 'friends'
  | 'premium'
  | 'community'
  | 'shop'
  | 'privacy';

/** Lokal-bazlı slug haritası (parametresiz sayfalar). */
export const ROUTE_SLUGS: Record<Locale, Record<RouteKey, string>> = {
  tr: {
    download: '/indir',
    home: '/',
    play: '/oyna',
    daily: '/gunluk',
    solver: '/cozucu',
    education: '/egitim',
    tutorial: '/nasil-oynanir',
    leaderboard: '/siralama',
    auth: '/auth',
    lobby: '/lobby',
    challenge: '/challenge',
    profile: '/profile',
    friends: '/friends',
    premium: '/premium',
    community: '/topluluk',
    shop: '/magaza',
    privacy: '/gizlilik',
  },
  en: {
    download: '/en/download',
    home: '/en',
    play: '/en/play',
    daily: '/en/daily',
    solver: '/en/solver',
    education: '/en/education',
    tutorial: '/en/tutorial',
    leaderboard: '/en/leaderboard',
    // Noindex sayfalar her dilde aynı path'i kullanır:
    auth: '/auth',
    lobby: '/lobby',
    challenge: '/challenge',
    profile: '/profile',
    friends: '/friends',
    premium: '/premium',
    community: '/en/community',
    shop: '/en/shop',
    privacy: '/en/privacy',
  },
  nl: {
    download: '/nl/downloaden',
    home: '/nl',
    play: '/nl/spelen',
    daily: '/nl/dagelijks',
    solver: '/nl/oplosser',
    education: '/nl/onderwijs',
    tutorial: '/nl/zelfstudie',
    leaderboard: '/nl/scorebord',
    auth: '/auth',
    lobby: '/lobby',
    challenge: '/challenge',
    profile: '/profile',
    friends: '/friends',
    premium: '/premium',
    community: '/nl/community',
    shop: '/nl/winkel',
    privacy: '/nl/privacy',
  },
  fr: {
    download: '/fr/telecharger',
    home: '/fr',
    play: '/fr/jouer',
    daily: '/fr/quotidien',
    solver: '/fr/solveur',
    education: '/fr/techniques',
    tutorial: '/fr/tutoriel',
    leaderboard: '/fr/classement',
    auth: '/auth',
    lobby: '/lobby',
    challenge: '/challenge',
    profile: '/profile',
    friends: '/friends',
    premium: '/premium',
    community: '/fr/communaute',
    shop: '/fr/boutique',
    privacy: '/fr/confidentialite',
  },
  de: {
    download: '/de/herunterladen',
    home: '/de',
    play: '/de/spielen',
    daily: '/de/taeglich',
    solver: '/de/loeser',
    education: '/de/techniken',
    tutorial: '/de/anleitung',
    leaderboard: '/de/bestenliste',
    auth: '/auth',
    lobby: '/lobby',
    challenge: '/challenge',
    profile: '/profile',
    friends: '/friends',
    premium: '/premium',
    community: '/de/community',
    shop: '/de/shop',
    privacy: '/de/datenschutz',
  },
  it: {
    download: '/it/scarica',
    home: '/it',
    play: '/it/gioca',
    daily: '/it/quotidiano',
    solver: '/it/risolutore',
    education: '/it/tecniche',
    tutorial: '/it/tutorial',
    leaderboard: '/it/classifica',
    auth: '/auth',
    lobby: '/lobby',
    challenge: '/challenge',
    profile: '/profile',
    friends: '/friends',
    premium: '/premium',
    community: '/it/community',
    shop: '/it/negozio',
    privacy: '/it/privacy',
  },
  pl: {
    download: '/pl/pobierz',
    home: '/pl',
    play: '/pl/graj',
    daily: '/pl/dzienne',
    solver: '/pl/solver',
    education: '/pl/techniki',
    tutorial: '/pl/poradnik',
    leaderboard: '/pl/ranking',
    auth: '/auth',
    lobby: '/lobby',
    challenge: '/challenge',
    profile: '/profile',
    friends: '/friends',
    premium: '/premium',
    community: '/pl/spolecznosc',
    shop: '/pl/sklep',
    privacy: '/pl/prywatnosc',
  },
  ru: {
    download: '/ru/skachat',
    // Kiril URL yerine Latin transliterasyon — SEO standart pratiği.
    home: '/ru',
    play: '/ru/igrat',
    daily: '/ru/ezhednevno',
    solver: '/ru/reshatel',
    education: '/ru/tekhniki',
    tutorial: '/ru/uchebnik',
    leaderboard: '/ru/tablitsa-liderov',
    auth: '/auth',
    lobby: '/lobby',
    challenge: '/challenge',
    profile: '/profile',
    friends: '/friends',
    premium: '/premium',
    community: '/ru/soobshchestvo',
    shop: '/ru/magazin',
    privacy: '/ru/konfidentsialnost',
  },
  es: {
    download: '/es/descargar',
    home: '/es',
    play: '/es/jugar',
    daily: '/es/diario',
    solver: '/es/solucionador',
    education: '/es/tecnicas',
    tutorial: '/es/tutorial',
    leaderboard: '/es/clasificacion',
    auth: '/auth',
    lobby: '/lobby',
    challenge: '/challenge',
    profile: '/profile',
    friends: '/friends',
    premium: '/premium',
    community: '/es/comunidad',
    shop: '/es/tienda',
    privacy: '/es/privacidad',
  },
};

/** URL → locale tespiti. Her dilin `home` slug'ına (veya alt yoluna) göre eşler; eşleşme yoksa tr. */
export function detectLocaleFromPath(pathname: string): Locale {
  for (const loc of ALL_LOCALES) {
    if (loc === 'tr') continue; // tr prefiks'siz varsayılan, son çare olarak döner
    const home = ROUTE_SLUGS[loc].home;
    if (pathname === home || pathname.startsWith(`${home}/`)) return loc;
  }
  return 'tr';
}

const LOCALE_DEFINING_KEYS: RouteKey[] = [
  'download',
  'home',
  'play',
  'daily',
  'solver',
  'education',
  'tutorial',
  'leaderboard',
  'community',
  'shop',
  'privacy',
];

/** URL dili açıkça belirtiyorsa locale döner; ortak/noindex path'lerde null döner. */
export function getLocaleFromLocalePath(pathname: string): Locale | null {
  for (const loc of ALL_LOCALES) {
    if (loc === 'tr') continue;
    const home = ROUTE_SLUGS[loc].home;
    if (pathname === home || pathname.startsWith(`${home}/`)) return loc;
  }

  for (const key of LOCALE_DEFINING_KEYS) {
    const slug = ROUTE_SLUGS.tr[key];
    if (slug === '/') {
      if (pathname === '/') return 'tr';
      continue;
    }
    if (pathname === slug || pathname.startsWith(`${slug}/`)) return 'tr';
  }

  return null;
}

/**
 * Locale + key → URL üretir.
 * `param` verilirse path'in sonuna eklenir (ör. teknik slug).
 */
export function routeFor(locale: Locale, key: RouteKey, param?: string | number): string {
  const base = ROUTE_SLUGS[locale][key];
  if (param == null || param === '') return base;
  return `${base.replace(/\/$/, '')}/${param}`;
}

/**
 * Mevcut bir path'i hedef dile çevirir. Örn:
 *   /egitim/x-wing  →  /en/education/x-wing
 *   /en/play        →  /oyna
 *   /room/abc       →  /room/abc  (noindex; aynı kalır)
 *   /user/123       →  /user/123  (aynı kalır)
 *
 * Bilinmeyen path için hedef dilin anasayfasına döner.
 */
export function swapLocaleInPath(pathname: string, target: Locale): string {
  const current: Locale = detectLocaleFromPath(pathname);
  if (current === target) return pathname;

  // Noindex / parametreli (room, user) — değişmez:
  if (pathname.startsWith('/room/') || pathname.startsWith('/user/')) {
    return pathname;
  }

  // Dinamik eğitim sayfası: /egitim/<slug> ↔ /en/education/<slug> ↔ /nl/onderwijs/<slug> ...
  const eduBase = ROUTE_SLUGS[current].education;
  if (pathname === eduBase || pathname.startsWith(`${eduBase}/`)) {
    const rest = pathname.slice(eduBase.length); // '' veya '/slug'
    return `${ROUTE_SLUGS[target].education}${rest}`;
  }

  // Statik slug eşleşmesi: hangi RouteKey'e karşılık geldiğini bul, hedef dilin karşılığını döndür.
  const currentSlugs = ROUTE_SLUGS[current];
  for (const key of Object.keys(currentSlugs) as RouteKey[]) {
    if (currentSlugs[key] === pathname) {
      return ROUTE_SLUGS[target][key];
    }
  }

  // Bilinmeyen path — hedef dilin anasayfasına dön.
  return ROUTE_SLUGS[target].home;
}

/**
 * Bir URL'in verilen dillerdeki (varsayılan: ALL_LOCALES) karşılıklarını döndürür
 * (hreflang üretimi için). Çağıran taraf genelde LIVE_LOCALES ile çağırır —
 * canlıya çıkmamış dillere hreflang verilmemesi gerektiği için.
 */
export function alternateUrls(pathname: string, site: string, locales: Locale[] = ALL_LOCALES): Record<Locale, string> {
  const current = detectLocaleFromPath(pathname);
  const result = {} as Record<Locale, string>;
  for (const loc of locales) {
    const path = current === loc ? pathname : swapLocaleInPath(pathname, loc);
    result[loc] = `${site}${path}`;
  }
  return result;
}

/**
 * Eski URL → yeni TR slug eşleştirmesi (redirect tablosu).
 * AppRouter'da Navigate replace kullanır; SEO açısından 301'e karşılık gelir
 * (canonical her zaman TR slug'a işaret ettiği için). Sadece TR için geçerli —
 * bu eski URL'ler hiçbir zaman EN/diğer dillerde var olmadı.
 */
export const LEGACY_REDIRECTS: Record<string, string> = {
  '/play': '/oyna',
  '/daily': '/gunluk',
  '/solver': '/cozucu',
  '/education': '/egitim',
  '/tutorial': '/nasil-oynanir',
  '/leaderboard': '/siralama',
};
